import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import themeDefaults from 'config/constants/themeDefaults'
import { uuidv4 } from 'src/components/utils'

// =======================================================
// Create Context
// =======================================================

const ThemeContext = React.createContext()
const ThemeUtilsContext = React.createContext()

// =======================================================
// Theme Props Reduce Util
// Given a map of theme props, this function reduces the
// props specified by each map entry value into a single
// object and returns it
// =======================================================

const reduceThemeProps = (componentThemeMap) => {
   let newTheme = {}

   // eslint-disable-next-line no-restricted-syntax
   for (const themeProps of componentThemeMap.values()) {
      newTheme = {
         ...newTheme,
         ...themeProps
      }
   }

   return newTheme
}

// =======================================================
// Theme Provider
// We use a Map structure to keep track of each consumer
// component's theme prop overrides. Since Maps store
// entries in the order they are inserted, we can simply
// reduce all the components' theme props to generate the
// net theme props, which is then passed through the
// context value.
// =======================================================

const ThemeProvider = ({ children }) => {
   const componentThemeMap = useRef(new Map([[null, themeDefaults]]))
   const [theme, setTheme] = useState(themeDefaults)

   const themeUtilsContextValue = useMemo(() => ({
      setThemePropsForComponent: (componentId, themeProps) => {
         componentThemeMap.current.set(componentId, themeProps)
         const newTheme = reduceThemeProps(componentThemeMap.current)
         setTheme(newTheme)
      },
      removeThemePropsForComponent: (componentId) => {
         componentThemeMap.current.delete(componentId)
         const newTheme = reduceThemeProps(componentThemeMap.current)
         setTheme(newTheme)
      }
   }), [])

   return (
      <ThemeUtilsContext.Provider value={themeUtilsContextValue}>
         <ThemeContext.Provider value={theme}>
            {children}
         </ThemeContext.Provider>
      </ThemeUtilsContext.Provider>
   )
}

ThemeProvider.propTypes = {
   children: PropTypes.node
}

ThemeProvider.defaultProps = {
   children: undefined
}

// =======================================================
// Friendly Theme Modifier Component
// =======================================================

const Theme = ({ children, ...themeProps }) => {
   const componentId = useRef(uuidv4())
   const {
      setThemePropsForComponent,
      removeThemePropsForComponent
   } = useContext(ThemeUtilsContext)

   // Let the ThemeProvider know whenever the theme props change
   useEffect(() => {
      setThemePropsForComponent(componentId.current, themeProps)
   }, [themeProps])

   // Let the ThemeProvider know when this component unmounts
   useEffect(() => () => {
      removeThemePropsForComponent(componentId.current)
   }, [])

   return (
      <>
         {children}
      </>
   )
}

Theme.propTypes = {
   children: PropTypes.node
}

Theme.defaultProps = {
   children: undefined
}

// =======================================================
// Export
// =======================================================

const MemoizedTheme = React.memo(Theme)

export {
   ThemeContext as default,
   MemoizedTheme as Theme,
   ThemeProvider,
   ThemeUtilsContext
}
